import { type ReactNode, useMemo, useState } from 'react'

import { ButtonRadio, type ButtonRadioItem } from '@/components/base/ButtonRadio'
import { Trans } from '@/components/Trans'
import { PurchasedEIP } from '@/models/eip'

import { ACLContent } from './EIPACL'
import { BillingContent } from './EIPBilling'
import { InformationContent } from './EIPInformation'
import { MonitorContent } from './EIPMonitor'
import { OperationLogContent } from './EIPOperationLog'
import { RelatedResourceContent, type SubTab } from './EIPRelatedResources'

interface EIPDetailProps {
  eip: PurchasedEIP
  regionDisplayName: string
}

type TopTab = 'relatedResource' | 'ipACL' | 'monitor' | 'billing' | 'information' | 'operationLog'

export function EIPDetail({ eip, regionDisplayName }: EIPDetailProps) {
  const [activeTab, setActiveTab] = useState<TopTab>('relatedResource')
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('instance')

  const tabItems = useMemo<(ButtonRadioItem<TopTab> & { hidden?: boolean })[]>(
    () => [
      {
        label: (
          <Trans ns="eip" i18nKey="detail.tabRelatedResource">
            Related Resource
          </Trans>
        ),
        value: 'relatedResource',
      },
      {
        label: (
          <Trans ns="eip" i18nKey="detail.tabIPACL">
            IP ACL
          </Trans>
        ),
        value: 'ipACL',
      },
      {
        label: (
          <Trans ns="eip" i18nKey="detail.tabMonitor">
            Monitor
          </Trans>
        ),
        value: 'monitor',
        hidden: true,
      },
      {
        label: (
          <Trans ns="eip" i18nKey="detail.tabBilling">
            Billing
          </Trans>
        ),
        value: 'billing',
      },
      {
        label: (
          <Trans ns="eip" i18nKey="detail.tabInformation">
            Information
          </Trans>
        ),
        value: 'information',
      },
      {
        label: (
          <Trans ns="eip" i18nKey="detail.tabOperationLog">
            Operation Log
          </Trans>
        ),
        value: 'operationLog',
        hidden: true,
      },
    ],
    [],
  )

  const subTabs = useMemo<{ label: ReactNode; value: SubTab; hidden?: boolean }[]>(
    () => [
      {
        label: (
          <Trans ns="eip" i18nKey="detail.subTabInstance">
            Instance
          </Trans>
        ),
        value: 'instance',
      },
      {
        label: (
          <Trans ns="eip" i18nKey="detail.subTabAutoDeploy">
            Auto Deploy
          </Trans>
        ),
        value: 'autoDeploy',
        hidden: true,
      },
      {
        label: (
          <Trans ns="eip" i18nKey="detail.subTabAutoScaling">
            Auto Scaling
          </Trans>
        ),
        value: 'autoScaling',
        hidden: true,
      },
    ],
    [],
  )

  const visibleTabItems = useMemo(() => tabItems.filter((tab) => !tab.hidden), [tabItems])
  const visibleSubTabs = useMemo(() => subTabs.filter((tab) => !tab.hidden), [subTabs])

  return (
    <div>
      <ButtonRadio
        size="small"
        value={activeTab}
        items={visibleTabItems}
        onChange={(value) => setActiveTab(value)}
      />

      <div className="mt-4">
        {activeTab === 'relatedResource' ? (
          <RelatedResourceContent
            eip={eip}
            activeSubTab={activeSubTab}
            onSubTabChange={setActiveSubTab}
            subTabs={visibleSubTabs}
          />
        ) : activeTab === 'ipACL' ? (
          <ACLContent eip={eip} />
        ) : activeTab === 'monitor' ? (
          <MonitorContent />
        ) : activeTab === 'information' ? (
          <InformationContent eip={eip} regionDisplayName={regionDisplayName} />
        ) : activeTab === 'operationLog' ? (
          <OperationLogContent />
        ) : activeTab === 'billing' ? (
          <BillingContent eip={eip} />
        ) : null}
      </div>
    </div>
  )
}
